import { parseZonedDateTime } from "@internationalized/date";
import dayjs from "dayjs";

import { guessTimezone } from "./tz";

type FormatOptions = {
  time?: boolean;
  zone?: string | null;
  showTz?: boolean;
  fromNow?: boolean;
};

export function format(date: string | Date, options?: FormatOptions): string;
export function format(
  date: string | Date | undefined | null,
  options?: FormatOptions,
): string | null;
export function format(
  date: string | Date | undefined | null,
  { time = true, zone, fromNow = false, showTz = false }: FormatOptions = {},
): string | null {
  const format = `MM/DD/YYYY${time ? ` HH:mm${showTz ? " z" : ""}` : ""}`;
  return formatDateTime({ date, time, zone, format, fromNow });
}

export function formatTzIso(date: string, zone?: string | null): string;
export function formatTzIso(
  date: string | null | undefined,
  zone?: string | null,
): string | null;
export function formatTzIso(
  date: string | null | undefined,
  zone?: string | null,
): string | null {
  if (!date) {
    return null;
  }

  const timeZone = zone || guessTimezone().zone;

  return dayjs.tz(date, timeZone).utc().format();
}

type FormatDateTimeProps = {
  time?: boolean;
  zone?: string | null;
  fromNow?: boolean;
  format: string;
};

type FormatDateTimeOptional = FormatDateTimeProps & {
  date: string | Date | undefined | null;
};

type FormatDateTimeRequired = FormatDateTimeProps & {
  date: string | Date;
};

export function formatDateTime(props: FormatDateTimeRequired): string;
export function formatDateTime(props: FormatDateTimeOptional): string | null;
export function formatDateTime({
  date,
  time,
  zone,
  format,
  fromNow,
}: FormatDateTimeOptional): string | null {
  if (!date) {
    return null;
  }

  if (typeof date === "string" && time) {
    if (date.match(/^.*\[[\w/]+\]$/)) {
      date = parseZonedDateTime(date).toDate();
    } else {
      date = new Date(date);
    }
  }

  let dayjsDate = dayjs(date);

  if (time) {
    dayjsDate = dayjsDate.tz(zone || undefined);
  } else {
    dayjsDate = dayjsDate.utc();
  }

  if (fromNow) {
    return dayjsDate.fromNow();
  }

  return dayjsDate.format(format);
}

export function fixLocalDate<T extends Date | null>(
  date: T,
): T extends Date ? Date : Date | null {
  if (!date) {
    return null as T extends Date ? Date : null;
  }
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() + offset);
}
